"use client";

import Image from "next/image";
import React from "react";

import CTAGroup from "@/components/CTAGroup";
import ContentContainer from "@/components/ContentContainer/ContentContainer";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import LazyIcon from "@/components/icons/LazyIcon";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import { parseMetadata } from "@/lib/parse-metadata";
import type { TAGULAR_ELEMENT } from "@/types/tagular";
import { VALUE_PROP_FEATURED_IMAGES } from "./featuredImages";
import { valuePropImagePath } from "./imagePath";
import { VALUE_PROP_IMAGES } from "./images";
import type {
  ValuePropBadge,
  ValuePropCard,
  ValuePropCopy,
  ValuePropMedia,
  ValuePropTextSize,
  ValuePropsBackgroundColor,
  ValuePropsCardStyle,
  ValuePropsSectionProps,
  ValuePropsTitleColor,
} from "./types";
import { mapValuePropsCtaButtonWidth } from "./types";

const TITLE_COLOR_CLASSES: Record<ValuePropsTitleColor, string> = {
  "neutral-base": "text-neutral-base",
  "theme1-base": "text-theme1-base",
};

const TITLE_POSITION_CLASSES = {
  left: "text-left",
  center: "text-center",
};

const SECTION_BACKGROUND_CLASSES: Record<ValuePropsBackgroundColor, string> = {
  white: "bg-material-neutral-base",
  "theme-base": "bg-material-theme1-base",
  "gradient-lavender": "",
};

const SECTION_BACKGROUND_IMAGES: Partial<
  Record<ValuePropsBackgroundColor, string>
> = {
  "gradient-lavender": `${valuePropImagePath}gradient-lavender.jpg`,
};

const CARD_STYLE_CLASSES: Record<ValuePropsCardStyle, string> = {
  transparent: "",
  white: "bg-material-neutral-base",
  "theme-base": "bg-material-theme1-base",
  "theme-bordered":
    "bg-material-theme1-base border-2 border-[rgba(90,35,185,0.15)] shadow-sm",
  "translucent-light": "bg-[rgba(246,246,249,0.30)]",
};

/**
 * Per-breakpoint size class maps. Each utility token must be written out as
 * a complete literal (e.g. `md:title-base-semibold`) so Tailwind can detect
 * it at build time. Dynamic interpolation would be purged.
 */
const TITLE_SIZE_CLASSES: Record<
  ValuePropTextSize,
  { mobile: string; tablet: string; desktop: string }
> = {
  xs: {
    mobile: "title-xs-semibold",
    tablet: "md:title-xs-semibold",
    desktop: "xl:title-xs-semibold",
  },
  sm: {
    mobile: "title-sm-semibold",
    tablet: "md:title-sm-semibold",
    desktop: "xl:title-sm-semibold",
  },
  base: {
    mobile: "title-base-semibold",
    tablet: "md:title-base-semibold",
    desktop: "xl:title-base-semibold",
  },
  lg: {
    mobile: "title-lg-semibold",
    tablet: "md:title-lg-semibold",
    desktop: "xl:title-lg-semibold",
  },
};

const SUBTITLE_SIZE_CLASSES: Record<
  ValuePropTextSize,
  { mobile: string; tablet: string; desktop: string }
> = {
  xs: {
    mobile: "body-xs-normal",
    tablet: "md:body-xs-normal",
    desktop: "xl:body-xs-normal",
  },
  sm: {
    mobile: "body-sm-normal",
    tablet: "md:body-sm-normal",
    desktop: "xl:body-sm-normal",
  },
  base: {
    mobile: "body-base-normal",
    tablet: "md:body-base-normal",
    desktop: "xl:body-base-normal",
  },
  lg: {
    mobile: "body-lg-normal",
    tablet: "md:body-lg-normal",
    desktop: "xl:body-lg-normal",
  },
};

// Badge tokens use the semibold body scale. `xs` falls back to the smaller
// `body-tiny-semibold` since the design system does not currently expose a
// `body-xs-semibold` token.
const BADGE_SIZE_CLASSES: Record<
  ValuePropTextSize,
  { mobile: string; tablet: string; desktop: string }
> = {
  xs: {
    mobile: "body-tiny-semibold",
    tablet: "md:body-tiny-semibold",
    desktop: "xl:body-tiny-semibold",
  },
  sm: {
    mobile: "body-sm-semibold",
    tablet: "md:body-sm-semibold",
    desktop: "xl:body-sm-semibold",
  },
  base: {
    mobile: "body-base-semibold",
    tablet: "md:body-base-semibold",
    desktop: "xl:body-base-semibold",
  },
  lg: {
    mobile: "body-lg-semibold",
    tablet: "md:body-lg-semibold",
    desktop: "xl:body-lg-semibold",
  },
};

// Section heading uses the bold typography scale, which only ships steps
// `lg / xl / 2xl / 3xl` in `globals.css`. The shared `xs|sm|base|lg` enum
// maps in order onto those bold tokens so editors can promote/demote the
// section heading per breakpoint without introducing a divergent enum.
//   xs   -> title-lg-bold   (smallest available bold step, matches default)
//   sm   -> title-xl-bold
//   base -> title-2xl-bold  (matches default desktop step)
//   lg   -> title-3xl-bold
const SECTION_TITLE_SIZE_CLASSES: Record<
  ValuePropTextSize,
  { mobile: string; tablet: string; desktop: string }
> = {
  xs: {
    mobile: "title-lg-bold",
    tablet: "md:title-lg-bold",
    desktop: "xl:title-lg-bold",
  },
  sm: {
    mobile: "title-xl-bold",
    tablet: "md:title-xl-bold",
    desktop: "xl:title-xl-bold",
  },
  base: {
    mobile: "title-2xl-bold",
    tablet: "md:title-2xl-bold",
    desktop: "xl:title-2xl-bold",
  },
  lg: {
    mobile: "title-3xl-bold",
    tablet: "md:title-3xl-bold",
    desktop: "xl:title-3xl-bold",
  },
};

type ResponsiveClassSlots = {
  mobile: string;
  tablet: string;
  desktop: string;
};

/**
 * Resolves a final responsive class string by overriding individual
 * breakpoints when the caller has supplied a `size` token, otherwise
 * falling back to the default per-breakpoint class.
 *
 * The lookup is null-safe so a malformed Monarch payload (e.g. a future
 * size token, a typo, or a schema drift) silently falls back to the
 * default class instead of crashing the entire section render.
 */
const resolveResponsiveTextSize = (
  defaults: ResponsiveClassSlots,
  overrides: ValuePropCopy | ValuePropBadge | undefined,
  sizeMap: Record<ValuePropTextSize, ResponsiveClassSlots>
): string => {
  const mobile = overrides?.mobile?.size
    ? (sizeMap[overrides.mobile.size]?.mobile ?? defaults.mobile)
    : defaults.mobile;
  const tablet = overrides?.tablet?.size
    ? (sizeMap[overrides.tablet.size]?.tablet ?? defaults.tablet)
    : defaults.tablet;
  const desktop = overrides?.desktop?.size
    ? (sizeMap[overrides.desktop.size]?.desktop ?? defaults.desktop)
    : defaults.desktop;

  return cn(mobile, tablet, desktop);
};

const getCardContainerClassName = ({
  cardStyle,
  isMobileListLeftLayout,
  isHorizontalLayout,
}: {
  cardStyle: ValuePropsCardStyle;
  isMobileListLeftLayout: boolean;
  isHorizontalLayout: boolean;
}) => {
  const layoutClass = isHorizontalLayout
    ? "flex items-center gap-2"
    : isMobileListLeftLayout
      ? "flex items-center gap-4 md:flex-col md:items-start md:gap-2"
      : "flex flex-col gap-2";

  if (cardStyle === "transparent") {
    return layoutClass;
  }

  if (cardStyle === "theme-bordered" || cardStyle === "translucent-light") {
    return cn("rounded-lg p-4", layoutClass, CARD_STYLE_CLASSES[cardStyle]);
  }

  return cn(
    "rounded-2xl p-4 md:p-6",
    layoutClass,
    CARD_STYLE_CLASSES[cardStyle]
  );
};

const getGridClassName = ({
  resolvedLayout,
  cardCount,
  hasFeaturedImage,
  isHorizontalLayout,
}: {
  resolvedLayout: "grid" | "list";
  cardCount: number;
  hasFeaturedImage: boolean;
  isHorizontalLayout: boolean;
}) => {
  return cn(
    "grid gap-4",
    !isHorizontalLayout && "md:gap-8",
    resolvedLayout === "list" ? "grid-cols-1" : "grid-cols-2",
    // When a featured image is shown the card column shrinks to ~50% width
    // starting at md, so the cards stack into a single column from md+
    // regardless of the tablet grid that would otherwise apply.
    hasFeaturedImage
      ? "md:grid-cols-1"
      : cardCount === 3
        ? "md:grid-cols-3"
        : "md:grid-cols-4"
  );
};

const resolveMobileLayout = (
  mobileLayout: ValuePropsSectionProps["mobileLayout"],
  cardCount: number
): "grid" | "list" => {
  if (mobileLayout === "grid" || mobileLayout === "list") {
    return mobileLayout;
  }

  return cardCount % 2 === 0 ? "grid" : "list";
};

const resolveCardMedia = (media: ValuePropMedia) => {
  if (media.type === "icon") {
    return {
      type: "icon" as const,
      icon: media.icon,
    };
  }

  if (media.type === "image" || media.type === "iconImage") {
    const imageData = VALUE_PROP_IMAGES[media.image];

    return imageData
      ? {
          type: media.type,
          ...imageData,
        }
      : null;
  }

  return null;
};

const getValuePropCardKey = (card: ValuePropCard, index: number) => {
  const keyBase = card.elementName?.trim() || card.title.copy.trim();

  return `${keyBase}-${index}`;
};

const hasVisualEnhancements = ({
  cards,
  title,
  subtitle,
  backgroundColor,
  cardStyle,
  ctaGroup,
  legal,
  featuredImage,
}: {
  cards: ValuePropCard[];
  title: ValuePropsSectionProps["title"];
  subtitle?: ValuePropsSectionProps["subtitle"];
  backgroundColor?: ValuePropsSectionProps["backgroundColor"];
  cardStyle?: ValuePropsSectionProps["cardStyle"];
  ctaGroup?: ValuePropsSectionProps["ctaGroup"];
  legal?: ValuePropsSectionProps["legal"];
  featuredImage?: ValuePropsSectionProps["featuredImage"];
}) => {
  return (
    title.position !== undefined ||
    title.color !== undefined ||
    backgroundColor !== undefined ||
    cardStyle !== undefined ||
    Boolean(ctaGroup?.ctas?.length) ||
    Boolean(legal?.copy?.trim()) ||
    Boolean(featuredImage?.key) ||
    Boolean(subtitle?.copy?.trim()) ||
    cards.some(
      (card) =>
        card.media.type !== "image" ||
        Boolean(card.subtitle?.copy?.trim()) ||
        Boolean(card.badge?.copy?.trim())
    )
  );
};

/**
 * Returns the default per-breakpoint title class slots before any per-card
 * size override is applied. Empty strings mean "inherit from earlier
 * breakpoint" (Tailwind responsive cascade).
 */
const getCardTitleDefaultSlots = ({
  mediaType,
  resolvedLayout,
  mobileListIconPlacement,
  isHorizontalLayout,
}: {
  mediaType: ValuePropMedia["type"];
  resolvedLayout: "grid" | "list";
  mobileListIconPlacement: ValuePropsSectionProps["mobileListIconPlacement"];
  isHorizontalLayout?: boolean;
}): ResponsiveClassSlots => {
  if (isHorizontalLayout) {
    return {
      mobile: "title-xs-semibold",
      tablet: "",
      desktop: "xl:title-sm-semibold",
    };
  }

  if (mediaType === "image") {
    return {
      mobile: "title-xs-semibold",
      tablet: "md:title-sm-semibold",
      desktop: "xl:title-base-semibold",
    };
  }

  if (resolvedLayout === "grid") {
    return {
      mobile: "title-tiny-semibold",
      tablet: "md:title-base-semibold",
      desktop: "",
    };
  }

  if (mobileListIconPlacement === "left") {
    return {
      mobile: "title-xs-semibold",
      tablet: "md:title-base-semibold",
      desktop: "",
    };
  }

  return {
    mobile: "title-sm-semibold",
    tablet: "md:title-base-semibold",
    desktop: "",
  };
};

const getCardSubtitleDefaultSlots = (
  isHorizontalLayout: boolean
): ResponsiveClassSlots =>
  isHorizontalLayout
    ? {
        mobile: "body-sm-normal",
        tablet: "",
        desktop: "xl:body-base-normal",
      }
    : {
        mobile: "body-xs-normal",
        tablet: "md:body-sm-normal",
        desktop: "",
      };

/**
 * Default per-breakpoint class slots for the section subtitle. Mirrors the
 * Figma reference (`body/base-normal` at every breakpoint) while still
 * allowing editors to override individual breakpoints via the per-bp
 * `mobile`/`tablet`/`desktop` size objects.
 * Movers (`gradient-lavender`) hardcodes mobile/tablet to 18px (`1.125rem`) below `xl`
 * to match the figma request.
 */
const getSectionSubtitleDefaultSlots = (
  backgroundColor: ValuePropsBackgroundColor
): ResponsiveClassSlots => {
  if (backgroundColor === "gradient-lavender") {
    return {
      mobile: "max-xl:text-[1.125rem] max-xl:leading-6",
      tablet: "",
      desktop: "",
    };
  }

  return {
    mobile: "body-base-normal",
    tablet: "",
    desktop: "",
  };
};

/**
 * Default per-breakpoint class slots for the section title heading. Matches
 * the prior fixed `title-lg-bold` / `xl:title-2xl-bold` pairing so omitting
 * `title.mobile/tablet/desktop` preserves the existing visual contract.
 * Movers (`gradient-lavender`) hardcodes mobile to 32px (`title-xl-bold`) and tablet to 36px (`md:title-2xl-bold`).
 */
const getSectionTitleDefaultSlots = (
  backgroundColor: ValuePropsBackgroundColor
): ResponsiveClassSlots => {
  if (backgroundColor === "gradient-lavender") {
    return {
      mobile: "title-xl-bold",
      tablet: "md:title-2xl-bold",
      desktop: "",
    };
  }

  return {
    mobile: "title-lg-bold",
    tablet: "",
    desktop: "xl:title-2xl-bold",
  };
};

const getCardBadgeDefaultSlots = (): ResponsiveClassSlots => ({
  mobile: "body-tiny-semibold",
  tablet: "md:body-sm-semibold",
  desktop: "",
});

const renderEnhancedMedia = (
  media: NonNullable<ReturnType<typeof resolveCardMedia>>,
  resolvedLayout: "grid" | "list",
  mobileListIconPlacement: ValuePropsSectionProps["mobileListIconPlacement"],
  compactIcons?: boolean
) => {
  if (media.type === "icon") {
    return (
      <LazyIcon
        name={media.icon}
        className={cn({
          "h-8 w-8 md:h-12 md:w-12": !compactIcons && resolvedLayout === "list",
          "h-6 w-6 md:h-12 md:w-12": !compactIcons && resolvedLayout === "grid",
          "h-8 w-8": compactIcons,
        })}
        style="outline"
        theme="purple"
      />
    );
  }

  if (media.type === "iconImage") {
    const mobileListIconImageClass =
      mobileListIconPlacement === "left" ? "h-8 w-8" : "h-6 w-6";

    return (
      <>
        <Image
          src={media.mobileSrc ?? media.src}
          alt={media.alt}
          width={
            resolvedLayout === "list" && mobileListIconPlacement === "left"
              ? 32
              : 24
          }
          height={
            resolvedLayout === "list" && mobileListIconPlacement === "left"
              ? 32
              : 24
          }
          className={cn(
            "block object-contain md:hidden",
            resolvedLayout === "list" ? mobileListIconImageClass : "h-6 w-6"
          )}
        />
        <Image
          src={media.mobileSrc ?? media.src}
          alt={media.alt}
          width={48}
          height={48}
          className="hidden h-12 w-12 object-contain md:block xl:hidden"
        />
        <Image
          src={media.src}
          alt={media.alt}
          width={media.width ?? 115}
          height={media.height ?? 48}
          className="hidden h-12 w-auto max-w-[115px] object-contain xl:block"
        />
      </>
    );
  }

  return (
    <div className="relative aspect-[300/169] w-full overflow-hidden rounded-xl">
      <Image
        src={media.src}
        alt={media.alt}
        fill
        sizes="(min-width: 1280px) 304px, (min-width: 768px) 25vw, 50vw"
        className="object-cover"
      />
    </div>
  );
};

const ValuePropsSection: React.FC<ValuePropsSectionProps> = ({
  title,
  subtitle,
  cards,
  elementName,
  mobileLayout,
  mobileListIconPlacement,
  backgroundColor,
  cardStyle,
  cardLayout,
  ctaGroup,
  legal,
  featuredImage,
  tagularProps,
}) => {
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const contextMonarchMetadata = useMonarchMetadata();
  const resolvedMonarchMetadata =
    tagularProps?.monarchMetadata ?? contextMonarchMetadata;
  const normalizedTitle = title?.copy?.trim() ?? "";
  const parsedTitle = parseMetadata(normalizedTitle);
  const normalizedSubtitle = subtitle?.copy?.trim() ?? "";
  const parsedSubtitle = normalizedSubtitle
    ? parseMetadata(normalizedSubtitle)
    : "";
  const normalizedCards = (cards ?? []).filter((card: ValuePropCard) =>
    card.title?.copy?.trim()
  );
  const shouldRender = normalizedTitle !== "" && normalizedCards.length > 0;
  const resolvedLayout = resolveMobileLayout(
    mobileLayout,
    normalizedCards.length
  );
  const shouldUseEnhancedLayout = hasVisualEnhancements({
    cards: normalizedCards,
    title,
    subtitle,
    backgroundColor,
    cardStyle,
    ctaGroup,
    legal,
    featuredImage,
  });
  const resolvedTitlePosition = title.position ?? "center";
  const resolvedTitleColor = title.color ?? "neutral-base";
  const resolvedBackgroundColor = backgroundColor ?? "white";
  const resolvedCardStyle = cardStyle ?? "transparent";
  const resolvedMobileListIconPlacement = mobileListIconPlacement ?? "top";
  const isHorizontalLayout = cardLayout === "horizontal";
  const sectionTitleClassName = resolveResponsiveTextSize(
    getSectionTitleDefaultSlots(resolvedBackgroundColor),
    title,
    SECTION_TITLE_SIZE_CLASSES
  );
  const sectionSubtitleClassName = resolveResponsiveTextSize(
    getSectionSubtitleDefaultSlots(resolvedBackgroundColor),
    subtitle,
    SUBTITLE_SIZE_CLASSES
  );
  const normalizedCtas =
    ctaGroup?.ctas
      ?.filter((cta) => cta.copy.trim() !== "")
      .map((cta) => ({
        ...cta,
        buttonWidth: mapValuePropsCtaButtonWidth(cta.buttonWidth),
      })) ?? [];
  const normalizedLegal = legal?.copy?.trim() ? legal : undefined;
  const resolvedFeaturedImage =
    featuredImage?.key && VALUE_PROP_FEATURED_IMAGES[featuredImage.key]
      ? {
          ...VALUE_PROP_FEATURED_IMAGES[featuredImage.key],
          position: featuredImage.position ?? "right",
        }
      : null;

  useElementViewed(
    sectionRef,
    {
      elementType: "card",
      location: "section",
      name: elementName,
      text: normalizedTitle,
      ...(tagularProps ?? {}),
      ...(resolvedMonarchMetadata && {
        monarchMetadata: resolvedMonarchMetadata,
      }),
    } as TAGULAR_ELEMENT,
    {
      threshold: 0.25,
    }
  );

  if (!shouldRender) {
    return null;
  }

  if (!shouldUseEnhancedLayout) {
    return (
      <ContentContainer spacing="none" className="py-6 md:py-8">
        <div ref={sectionRef} className="flex flex-col gap-6 md:gap-8">
          <h2
            className={cn(
              sectionTitleClassName,
              "text-neutral-base text-center"
            )}
          >
            {normalizedTitle}
          </h2>
          <div
            className={cn(
              "grid gap-4 md:gap-8",
              resolvedLayout === "list" ? "grid-cols-1" : "grid-cols-2",
              "md:grid-cols-4"
            )}
          >
            {normalizedCards.map((card, index) => {
              const media = resolveCardMedia(card.media);
              const cardKey = getValuePropCardKey(card, index);

              return (
                <div key={cardKey} className="flex flex-col gap-2">
                  <div className="relative aspect-[300/169] w-full overflow-hidden rounded-lg">
                    {media?.type === "image" && (
                      <Image
                        src={media.src}
                        alt={media.alt}
                        fill
                        sizes="(min-width: 1280px) 304px, (min-width: 768px) 25vw, 50vw"
                        className="object-cover"
                      />
                    )}
                  </div>
                  <p className="title-xs-semibold md:title-sm-semibold xl:title-base-semibold text-neutral-base">
                    {card.title.copy}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </ContentContainer>
    );
  }

  const renderCardsGrid = (
    <div
      className={getGridClassName({
        resolvedLayout,
        cardCount: normalizedCards.length,
        hasFeaturedImage: Boolean(resolvedFeaturedImage),
        isHorizontalLayout,
      })}
    >
      {normalizedCards.map((card, index) => {
        const media = resolveCardMedia(card.media);
        const cardKey = getValuePropCardKey(card, index);
        const badgeCopy = card.badge?.copy?.trim();
        const subtitleCopy = card.subtitle?.copy?.trim();
        const isMobileListLeftLayout =
          resolvedLayout === "list" &&
          resolvedMobileListIconPlacement === "left" &&
          card.media.type !== "image";
        const isMobileLeftBadgePlacement =
          isMobileListLeftLayout && Boolean(badgeCopy);
        const parsedCardTitle = parseMetadata(card.title.copy);
        const titleDefaults = getCardTitleDefaultSlots({
          mediaType: card.media.type,
          resolvedLayout,
          mobileListIconPlacement: resolvedMobileListIconPlacement,
          isHorizontalLayout,
        });
        const cardTitleClassName = resolveResponsiveTextSize(
          titleDefaults,
          card.title,
          TITLE_SIZE_CLASSES
        );
        const subtitleDefaults =
          getCardSubtitleDefaultSlots(isHorizontalLayout);
        const subtitleClassName = resolveResponsiveTextSize(
          subtitleDefaults,
          card.subtitle,
          SUBTITLE_SIZE_CLASSES
        );
        const badgeDefaults = getCardBadgeDefaultSlots();
        const badgeClassName = resolveResponsiveTextSize(
          badgeDefaults,
          card.badge,
          BADGE_SIZE_CLASSES
        );

        return (
          <article
            key={cardKey}
            className={getCardContainerClassName({
              cardStyle: resolvedCardStyle,
              isMobileListLeftLayout,
              isHorizontalLayout,
            })}
          >
            <div
              className={cn(
                "flex gap-3",
                isMobileListLeftLayout
                  ? "shrink-0 items-center justify-center md:w-full md:justify-between"
                  : "items-center justify-between"
              )}
            >
              {media
                ? renderEnhancedMedia(
                    media,
                    resolvedLayout,
                    resolvedMobileListIconPlacement,
                    isHorizontalLayout
                  )
                : null}
              {badgeCopy ? (
                <span
                  className={cn(
                    badgeClassName,
                    "text-theme1-base",
                    isMobileLeftBadgePlacement ? "hidden md:inline" : ""
                  )}
                >
                  {badgeCopy}
                </span>
              ) : null}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {isMobileLeftBadgePlacement ? (
                <span
                  className={cn(badgeClassName, "text-theme1-base md:hidden")}
                >
                  {badgeCopy}
                </span>
              ) : null}
              <p
                className={cn(cardTitleClassName, "text-neutral-base")}
                dangerouslySetInnerHTML={{ __html: parsedCardTitle }}
              />
              {subtitleCopy ? (
                <p className={cn(subtitleClassName, "text-neutral-base")}>
                  {subtitleCopy}
                </p>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );

  const renderFeaturedImage = resolvedFeaturedImage ? (
    <div
      data-testid="value-props-featured-image"
      className="hidden md:block md:flex-1 md:basis-1/2 xl:basis-[58%]"
    >
      {/* h-full + parent flex items-stretch makes the image fill the row's height,
          which is dictated by the cards column. object-cover crops as needed. */}
      <div className="relative h-full w-full overflow-hidden rounded-2xl">
        <Image
          src={resolvedFeaturedImage.src}
          alt={resolvedFeaturedImage.alt}
          fill
          sizes="(min-width: 1280px) 58vw, (min-width: 768px) 50vw, 0px"
          className="object-cover"
        />
      </div>
    </div>
  ) : null;

  const backgroundImageSrc = SECTION_BACKGROUND_IMAGES[resolvedBackgroundColor];

  return (
    <div
      className={cn(
        "relative",
        SECTION_BACKGROUND_CLASSES[resolvedBackgroundColor]
      )}
    >
      {backgroundImageSrc ? (
        <Image
          src={backgroundImageSrc}
          alt=""
          aria-hidden="true"
          fill
          sizes="100vw"
          className="pointer-events-none select-none object-cover"
        />
      ) : null}
      <ContentContainer
        spacing="none"
        className={cn(
          "relative",
          isHorizontalLayout ? "py-8 md:py-10" : "py-10 md:py-16"
        )}
      >
        <div
          ref={sectionRef}
          className={cn(
            "flex flex-col",
            isHorizontalLayout ? "gap-4 md:gap-6 xl:gap-8" : "gap-6 md:gap-8"
          )}
        >
          {parsedSubtitle ? (
            <div
              className={cn(
                "flex flex-col gap-4",
                TITLE_POSITION_CLASSES[resolvedTitlePosition]
              )}
            >
              <h2
                className={cn(
                  sectionTitleClassName,
                  TITLE_COLOR_CLASSES[resolvedTitleColor]
                )}
                dangerouslySetInnerHTML={{ __html: parsedTitle }}
              />
              <p
                className={cn(
                  sectionSubtitleClassName,
                  TITLE_COLOR_CLASSES[resolvedTitleColor]
                )}
                dangerouslySetInnerHTML={{ __html: parsedSubtitle }}
              />
            </div>
          ) : (
            <h2
              className={cn(
                sectionTitleClassName,
                TITLE_COLOR_CLASSES[resolvedTitleColor],
                TITLE_POSITION_CLASSES[resolvedTitlePosition]
              )}
              dangerouslySetInnerHTML={{ __html: parsedTitle }}
            />
          )}
          {resolvedFeaturedImage ? (
            <div
              className={cn(
                "flex flex-col gap-6 md:flex-row xl:gap-10",
                resolvedFeaturedImage.position === "left" &&
                  "md:flex-row-reverse"
              )}
            >
              <div className="md:flex-1 md:basis-1/2 xl:basis-[42%]">
                {renderCardsGrid}
              </div>
              {renderFeaturedImage}
            </div>
          ) : (
            renderCardsGrid
          )}
          {normalizedCtas.length > 0 ? (
            <CTAGroup
              ctas={normalizedCtas}
              alignment={ctaGroup?.alignment ?? "center"}
              defaultDisplay="fill"
              defaultTone="neutral"
              defaultSize="md"
              location="section"
              elementName={elementName}
              tagularProps={tagularProps}
            />
          ) : null}
          {normalizedLegal ? (
            <LegalDisclaimer
              disclaimers={normalizedLegal.copy}
              variant="compact"
              textAlign="center"
              className="body-xs-normal text-neutral-base/80"
            />
          ) : null}
        </div>
      </ContentContainer>
    </div>
  );
};

export default ValuePropsSection;
